import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import { fetchPromptLibrary } from '../services/api/commonData';

// Define types for your data structure
interface Prompt {
  id: string;
  sequence: number;
  text: string;
}

interface Category {
  Id: string;
  Sequence: number;
  Title: string;
  Description: string;
  Prompts: Prompt[];
}
interface ConfigItem {
  Id: string;
  ConfigValue: any; // Adjust this based on actual data structure
}

interface AddFromPromptLibraryProps {
  isPLibraryModalOpen: boolean;
  setIsPromptLibraryModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddFromPromptLibrary: React.FC<AddFromPromptLibraryProps> = ({
  isPLibraryModalOpen,
  setIsPromptLibraryModalOpen,
}) => {
  const [promptData, setPromptData] = useState<Category[]>([]); // Set the type to Category[]
  const [filteredPromptData, setFilteredPromptData] = useState<Category[]>([]); // Set filtered data
  const [searchText, setSearchText] = useState('');
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

  useEffect(() => {
    // Call getPromptData once when the component mounts
    getPromptData();
  }, []);

  useEffect(() => {
    // If there's no search text, show all categories
    if (searchText.trim() === '') {
      setFilteredPromptData(promptData);
    } else {
      // Filter categories based on searchText matching the title only (case-insensitive)
      const filteredData = promptData.filter((category) =>
        category.Title.toLowerCase().includes(searchText.toLowerCase())
      );

      setFilteredPromptData(filteredData); // Set the filtered categories based on title match
    }
  }, [searchText, promptData]);

  const getPromptData = async () => {
    try {
      const getCachedData = (key: string) => {
        const item = sessionStorage.getItem(key);
        if (!item) return null;
        const { value, timestamp } = JSON.parse(item);
        if (Date.now() - timestamp > 2 * 24 * 60 * 60 * 1000) { // 2 days cache
          sessionStorage.removeItem(key);
          return null;
        }
        return value;
      };
      const config = getCachedData('systemConfig');
      
      if(config){
        const promptLibraryEntry = config.find((item: ConfigItem) => item.Id === "prompt_library");
        if (promptLibraryEntry && Array.isArray(promptLibraryEntry.ConfigValue)) {
          const transformedData: Category[] = promptLibraryEntry.ConfigValue.map(
            (item: { Id: string; Name: string; Description: string; ConfigValue: Prompt[] }, index: number) => ({
              Id: item.Id,
              Sequence: index + 1,
              Title: item.Name,
              Description: item.Description,
              Prompts: Array.isArray(item.ConfigValue)
                ? item.ConfigValue.map((prompt: Prompt) => ({
                    id: prompt.id,
                    sequence: prompt.sequence,
                    text: prompt.text,
                  }))
                : []
            })
          );
  
          setPromptData(transformedData);
          // console.log(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching prompt data', error);
    }
  };

  const handleOverlayClick = () => {
    setIsPromptLibraryModalOpen(false);
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleCopyClick = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(id);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-50 ${
        isPLibraryModalOpen ? 'block' : 'hidden'
      }`}
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white w-[25%] h-[94%] shadow-lg transform transition-transform duration-300 ease-in-out overflow-y-scroll"
        onClick={handleModalClick}
      >
        <div className="p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold mb-4">Prompt Library</h2>
            <button
              onClick={() => setIsPromptLibraryModalOpen(false)}
              className="text-black-500 hover:text-red-700"
            >
              <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
            </button>
          </div>

          <p className="text-gray-600 mb-4">
            Choose from a curated list of commonly used prompt sentences specifically worded to
            instruct the chatbot to respond or behave a specific way.
          </p>

          <div className="p-4">
            <input
              type="text"
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search by title..."
              className="border p-2 rounded mb-4 w-[100%]"
            />
          </div>

          <div className="space-y-6">
            {filteredPromptData.length === 0 ? (
              <p className="text-center text-gray-500">No matching categories found.</p>
            ) : (
              filteredPromptData.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-8">
                  <h3 className="text-lg font-medium mb-2">{category.Title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{category.Description}</p>

                  {/* Display prompts for the category */}
                  {category.Prompts.length === 0 ? (
                    <p className="text-sm text-gray-500">No prompts in this category.</p>
                  ) : (
                    category.Prompts.map((prompt, promptIndex) => {
                      const promptId = `${categoryIndex}-${promptIndex}`;
                      return (
                        <div
                          key={promptId}
                          className="bg-gray-50 p-4 rounded-lg mb-4 relative group"
                        >
                          <div className="flex items-start gap-2">
                            <span className="bg-gray-900 rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 text-white">
                              {prompt.sequence}
                            </span>
                            <p
  className="text-sm pr-8"
  dangerouslySetInnerHTML={{
    __html: prompt.text
      .replace(/\\n|(\r?\n)/g, '<br />') // Replace newlines with <br />
      .replace(/{{(.*?)}}/g, (match, p1) => {
        // For each match of {{...}}, wrap only the inside content in a span with blue text color
        return `{{<span style="color: blue;">${p1}</span>}}`;
      }),
  }}
></p>




                           
                            <button
                              onClick={() => handleCopyClick(prompt.text, promptId)}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FontAwesomeIcon
                                icon={copiedIndex === promptId ? faCheck : faCopy}
                                className={`h-4 w-4 ${copiedIndex === promptId ? 'text-green-500' : 'text-gray-500 hover:text-gray-700'}`}
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFromPromptLibrary;
